import {
  FormattedString,
  FormInputGroupLayout,
  FormInputGroupLayoutContextProvider,
} from "o4a-react";
import * as React from "react";
import { Stack } from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { networkOverviewUrl } from "../../constants/docConstants";
import { maxNetworkLinkCidrPrefix, minCidrPrefix } from "../../helpers/resourceHelper";
import {
  classDReservedIPAddressRange,
  classEReservedIPAddressRange,
  ociReservedIPAddressRange,
} from "../../helpers/validationHelper";
import { NetworkAddressInput, NetworkAddressInputProps } from "../NetworkAddressInput/NetworkAddressInput";

export interface BaseOciCidrProps {
  /**
   * The numerical max value of the CIDR prefix (/23)
   * When used for networkLink flows the default is sufficient
   * When used with mcvcn flows, the default should be (/28)
   * @default 23
   */
  maxPrefix?: number;
  /**
   * The numerical min value of the CIDR prefix (/16)
   * @default 16
   */
  minPrefix?: number;
  /**
   * We assume that the list of CIDRs provided do not overlap with themselves
   */
  excludeCidrs?: string[];
  /**
   * We assume that the list of CIDRs provided do not overlap with themselves
   * The only difference between this and excludeCidrs is the error message displayed
   */
  reservedCidrs?: string[];
}

type BaseIpInputProps = Pick<NetworkAddressInputProps,
"defaultValue"
| "disabled"
| "fieldName"
| "groupName"
| "label"
| "onChange"
| "required"
| "validator"
>;

export type OciCidrProps = BaseOciCidrProps & BaseIpInputProps

/**
 * In order to access the sub fields you must use the fieldName you provided
 * concatenated with any of th
 */
export const OciCidr = ({
  defaultValue,
  disabled,
  excludeCidrs,
  fieldName,
  groupName,
  label,
  maxPrefix = maxNetworkLinkCidrPrefix,
  minPrefix = minCidrPrefix,
  onChange,
  validator,
  reservedCidrs = [],
  required,
}: OciCidrProps): JSX.Element => {
  const internalReservedAddresses = [
    ...reservedCidrs,
    classDReservedIPAddressRange,
    classEReservedIPAddressRange,
    ociReservedIPAddressRange,
  ];

  return (
    <FormInputGroupLayoutContextProvider value={{ groupName: groupName || "", layout: FormInputGroupLayout.COMPACT }}>
      <Stack horizontal>
        <NetworkAddressInput
          disabled={disabled}
          tooltip={FormattedString(
            {
              inputText:
                Messages.hints.toolTipAddressSpace(
                  minPrefix.toString(),
                  maxPrefix.toString(),
                  networkOverviewUrl,
                ),
            },
          ) as unknown as string}
          label={label}
          defaultValue={defaultValue}
          onChange={onChange}
          validator={validator}
          fieldName={fieldName}
          groupName={groupName}
          required={required}
          validation={{
            containsCidrBlock: true,
            containsIpAddress: false,
            excludeAddresses: excludeCidrs,
            reservedAddresses: internalReservedAddresses,
            prefixRange: {
              min: minPrefix,
              max: maxPrefix,
            },
          }}
        />
      </Stack>
    </FormInputGroupLayoutContextProvider>
  );
};
